import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield, ShieldCheck, Camera, FileText, MapPin, Users, Home as HomeIcon,
  Building2, Wallet, Wrench, ArrowRight, CheckCircle2, Star, Play,
} from "lucide-react";
import { waLink, api, formatApiError } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";

const HERO_IMG = "https://images.pexels.com/photos/32355381/pexels-photo-32355381.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";
const INSPECTOR_IMG = "https://images.pexels.com/photos/28196491/pexels-photo-28196491.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const NRI_IMG = "https://images.pexels.com/photos/7919624/pexels-photo-7919624.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SENIOR_IMG = "https://images.pexels.com/photos/19811308/pexels-photo-19811308.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PLOT_IMG = "https://images.pexels.com/photos/37822408/pexels-photo-37822408.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const HANDSHAKE_IMG = "https://images.pexels.com/photos/9870231/pexels-photo-9870231.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Home() {
  return (
    <div data-testid="page-home">
      <Hero />
      <TrustStrip />
      <DemographicCards />
      <ProblemBand />
      <Services />
      <HowItWorks />
      <DashboardPreview />
      <PricingPreview />
      <GoogleReviews />
      <Testimonials />
      <SampleReport />
      <FaqPreview />
      <FinalCTA />
    </div>
  );
}

/* ---------- Hero ---------- */
function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-brand-navy text-white">
      <img src={HERO_IMG} alt="Chennai apartment" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/95 to-brand-navy/70" />
      <div className="relative container-x py-24 md:py-36 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8 animate-fade-up">
          <div className="eyebrow !text-brand-cyan">Premium Property Care · Chennai</div>
          <h1 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Your Property in Chennai. <span className="text-brand-cyan">Guarded, Maintained and Managed</span> — Even When You Are Miles Away.
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80 leading-relaxed">
            Whether you live abroad, outside Chennai, or simply cannot visit your property regularly, Hi-Tech Realty gives you a trusted local team on the ground. We monitor, manage, document and protect your property with clear reports, real photos, verified visits and responsible follow-up.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link data-testid="hero-book-review" to="/contact" className="btn-cyan">Book a Property Review <ArrowRight size={14} /></Link>
            <a
              data-testid="hero-whatsapp"
              href={waLink("Hi Hi-Tech Realty, I want to book a property review in Chennai.")}
              target="_blank" rel="noreferrer"
              className="btn-whatsapp"
            >
              WhatsApp Our Chennai Team
            </a>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
            {[
              { n: "450+", l: "Properties Monitored" },
              { n: "1,200+", l: "Reports Delivered" },
              { n: "25+", l: "Localities Covered" },
              { n: "24 hrs", l: "Report Turnaround" },
            ].map((s) => (
              <div key={s.l} className="border-l-2 border-brand-cyan pl-3">
                <div className="font-display text-3xl font-bold">{s.n}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-cyan font-semibold mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-4 hidden lg:block">
          <div className="relative">
            <div className="bg-white text-brand-navy p-6 shadow-2xl border-l-4 border-brand-cyan">
              <div className="eyebrow !text-brand-cyan">Sample Inspection Snapshot</div>
              <div className="mt-3 font-display text-xl font-bold">2BHK, Velachery</div>
              <div className="mt-1 text-xs text-brand-slate/70">Visit · 08 Dec 2025 · Ravi Kumar</div>
              <ul className="mt-5 space-y-3 text-sm">
                <li className="flex items-start gap-2"><CheckCircle2 size={18} className="text-emerald-500 mt-0.5"/> Door, locks, windows verified</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={18} className="text-emerald-500 mt-0.5"/> EB meter reading captured</li>
                <li className="flex items-start gap-2"><Camera size={18} className="text-brand-cyan mt-0.5"/> 14 geo-tagged photos uploaded</li>
                <li className="flex items-start gap-2"><Shield size={18} className="text-amber-500 mt-0.5"/> Minor seepage near balcony</li>
              </ul>
              <div className="mt-5 flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-amber-600">Status · Amber</span>
                <Link to="/contact" className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-cyan hover:underline">Get Report</Link>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-brand-cyan text-brand-navy px-4 py-3 font-display font-bold">
              No vague updates. We show you proof.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  const items = ["Chennai-Based Field Team", "Geo-Tagged Inspection Reports", "Rental & Plot Management", "Trusted by NRIs & Seniors"];
  return (
    <div className="bg-white border-y border-brand-border">
      <div className="container-x py-5 flex flex-wrap items-center justify-between gap-4 text-brand-navy">
        {items.map((i) => (
          <div key={i} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold">
            <ShieldCheck size={16} className="text-brand-cyan" /> {i}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Demographic Cards ---------- */
function DemographicCards() {
  const cards = [
    {
      tag: "The NRI Dilemma",
      img: NRI_IMG,
      body: "You should not have to wake up at midnight to handle tenant complaints or repair calls in Chennai. Our local team acts as your trusted representative — verified updates, inspection reports, tenant coordination and maintenance support while you stay abroad with peace of mind.",
      cta: "Manage My Property from Abroad",
      to: "/nri-corner",
      testid: "card-nri",
    },
    {
      tag: "The Senior Citizen Promise",
      img: SENIOR_IMG,
      body: "Property management should not become a physical or emotional burden. We help senior citizens with trusted local assistance for tenant handling, repair coordination, utility reminders, documentation support and property visits — with transparent family updates.",
      cta: "Get Senior Property Support",
      to: "/services/senior-citizen",
      testid: "card-senior",
    },
    {
      tag: "The Plot Owner Safeguard",
      img: PLOT_IMG,
      body: "Vacant land needs regular physical presence. We monitor your plot with scheduled visits, boundary checks, photo/video reports, fencing status updates and encroachment alerts — so your investment never sits unattended.",
      cta: "Protect My Vacant Plot",
      to: "/services/plot-monitoring",
      testid: "card-plot",
    },
  ];
  return (
    <section className="section-pad bg-brand-ice" data-testid="section-demographics">
      <div className="max-w-4xl">
        <div className="eyebrow">Built for Owners Who Cannot Be Local</div>
        <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-brand-navy leading-tight">
          One trusted team. Three kinds of peace of mind.
        </h2>
      </div>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {cards.map((c) => (
          <article key={c.tag} data-testid={c.testid} className="group card-flat relative">
            <div className="h-2 bg-transparent group-hover:bg-brand-cyan transition-colors" />
            <img src={c.img} alt={c.tag} className="w-full h-48 object-cover" />
            <div className="p-8">
              <div className="eyebrow">{c.tag}</div>
              <p className="mt-4 text-sm leading-relaxed text-brand-slate/85">{c.body}</p>
              <Link to={c.to} className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-brand-navy hover:text-brand-cyan">
                {c.cta} <ArrowRight size={14} />
              </Link>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ---------- Problem band ---------- */
function ProblemBand() {
  return (
    <section className="bg-brand-navy text-white">
      <div className="container-x py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="eyebrow !text-brand-cyan">The Problem We Solve</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold leading-tight">
            When property is unattended, small issues become expensive.
          </h2>
          <p className="mt-5 text-white/80 leading-relaxed">
            Tenant problems escalate. A leaking pipe becomes a damaged wall. Property tax piles up. A vacant plot quietly turns into an encroachment notice. Hi-Tech Realty closes that gap with verified local presence and documented follow-up.
          </p>
          <Link to="/services" data-testid="problem-services-link" className="mt-7 btn-cyan">See How We Help</Link>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[
            { Icon: Wrench, t: "Unattended Repairs" },
            { Icon: Wallet, t: "Missed Payments" },
            { Icon: MapPin, t: "Encroachment Risk" },
            { Icon: FileText, t: "Document Pile-Ups" },
          ].map(({ Icon, t }) => (
            <div key={t} className="border border-white/20 p-6">
              <Icon size={26} className="text-brand-cyan" />
              <div className="mt-4 font-display text-2xl font-bold">{t}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Services snapshot ---------- */
function Services() {
  const items = [
    { Icon: MapPin, t: "Plot Monitoring", b: "Scheduled visits, boundary checks and encroachment alerts for vacant land.", to: "/services/plot-monitoring", testid: "service-plot" },
    { Icon: HomeIcon, t: "Rental Management", b: "Tenant coordination, rent follow-up, agreements and maintenance for rented homes.", to: "/services/rental-management", testid: "service-rental" },
    { Icon: Shield, t: "Vacant Home Monitoring", b: "Periodic checks for seepage, security and upkeep of locked flats and houses.", to: "/services/vacant-home", testid: "service-vacant" },
    { Icon: Users, t: "Senior Citizen Property Care", b: "Trusted in-person help with tenants, repairs and documentation.", to: "/services/senior-citizen", testid: "service-senior" },
  ];
  return (
    <section className="section-pad" data-testid="section-services">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="eyebrow">Services Snapshot</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-brand-navy">Choose the right care for your property.</h2>
        </div>
        <Link to="/services" className="btn-ghost-navy">View All Services <ArrowRight size={14} /></Link>
      </div>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map(({ Icon, t, b, to, testid }) => (
          <Link key={t} to={to} data-testid={testid} className="card-flat p-7 group hover:border-brand-cyan transition-colors">
            <div className="w-12 h-12 bg-brand-ice grid place-items-center border border-brand-border group-hover:bg-brand-navy group-hover:border-brand-navy transition-colors">
              <Icon size={22} className="text-brand-navy group-hover:text-brand-cyan" />
            </div>
            <div className="mt-5 font-display text-2xl font-bold text-brand-navy">{t}</div>
            <p className="mt-2 text-sm leading-relaxed text-brand-slate/80">{b}</p>
            <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-brand-cyan">
              Explore <ArrowRight size={14} />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ---------- How it works ---------- */
function HowItWorks() {
  const steps = [
    { n: "01", t: "Share Your Property Details", b: "Send us location, ownership proof, photos and your main concern — tenant, vacant plot, maintenance, rent or documentation." },
    { n: "02", t: "We Inspect & Create a Care Plan", b: "Our Chennai field team visits the property, records its condition, checks visible risks and recommends the right plan." },
    { n: "03", t: "You Receive Verified Updates", b: "You get inspection reports, geo-tagged photos, videos and rent updates via WhatsApp, email or your owner dashboard." },
  ];
  return (
    <section className="bg-white border-y border-brand-border" data-testid="section-how-it-works">
      <div className="container-x py-20 md:py-28">
        <div className="max-w-3xl">
          <div className="eyebrow">How It Works</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-brand-navy">The 3-Step Peace of Mind Process.</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-8 relative">
          <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-brand-border" />
          {steps.map((s) => (
            <div key={s.n} className="relative bg-white">
              <div className="w-16 h-16 bg-brand-navy text-white grid place-items-center font-display text-2xl font-bold border-4 border-brand-ice">
                {s.n}
              </div>
              <div className="mt-6 font-display text-2xl font-bold text-brand-navy">{s.t}</div>
              <p className="mt-3 text-sm leading-relaxed text-brand-slate/80">{s.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Dashboard preview ---------- */
function DashboardPreview() {
  return (
    <section className="section-pad" data-testid="section-dashboard-preview">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="eyebrow">My Property Care Dashboard</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-brand-navy">We do not just say we visited. We show you proof.</h2>
          <p className="mt-5 text-brand-slate/85 leading-relaxed">
            Owners receive a clean dashboard with verified inspection reports, geo-tagged photos, rental statements, open maintenance tickets and a documents vault — everything documented, nothing assumed.
          </p>
          <Link to="/login/owner" data-testid="dashboard-preview-login" className="mt-7 btn-navy">Owner Login</Link>
        </div>
        <div className="lg:col-span-7">
          <div className="card-flat p-6 md:p-8">
            <div className="flex items-center justify-between border-b border-brand-border pb-4">
              <div className="font-display text-xl font-bold text-brand-navy">2BHK, Velachery · Phoenix Towers</div>
              <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-amber-600">Health · Amber</span>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <Tile title="Latest Inspection" sub="08 Dec 2025 · Ravi Kumar" Icon={Camera} />
              <Tile title="Next Visit" sub="22 Dec 2025 · Monthly" Icon={MapPin} />
              <Tile title="Rent Received" sub="₹32,000 · December" Icon={Wallet} />
              <Tile title="Open Tickets" sub="2 active · 1 with vendor" Icon={Wrench} />
            </div>
            <div className="mt-6 border border-brand-border p-5">
              <div className="eyebrow">Documents Vault</div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-brand-slate/80">
                <div className="flex items-center gap-2"><FileText size={14}/> Sale Deed.pdf</div>
                <div className="flex items-center gap-2"><FileText size={14}/> EC Certificate.pdf</div>
                <div className="flex items-center gap-2"><FileText size={14}/> Rental Agreement.pdf</div>
                <div className="flex items-center gap-2"><FileText size={14}/> Tax Receipt 2024-25.pdf</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Tile({ title, sub, Icon }) {
  return (
    <div className="border border-brand-border p-5 hover:border-brand-cyan transition-colors">
      <Icon size={20} className="text-brand-cyan" />
      <div className="mt-3 font-semibold text-brand-navy text-sm">{title}</div>
      <div className="text-xs text-brand-slate/70 mt-1">{sub}</div>
    </div>
  );
}

/* ---------- Pricing preview ---------- */
function PricingPreview() {
  const tiers = [
    { name: "Vacant Plot Shield", price: "₹7,500", per: "/year", desc: "Scheduled site visits, boundary checks, encroachment alerts.", testid: "tier-plot" },
    { name: "Vacant Home Watch", price: "₹15,000", per: "/year", desc: "Periodic inspection, seepage & security checks, photo updates.", testid: "tier-vacant" },
    { name: "Comprehensive Rental Management", price: "₹1,250", per: "/month", desc: "Tenant sourcing, rent follow-up, maintenance coordination.", featured: true, testid: "tier-rental" },
    { name: "Premium Concierge Care", price: "Custom", per: "", desc: "Dedicated manager, legal, vendor & family update calls.", testid: "tier-concierge" },
  ];
  return (
    <section className="bg-brand-ice section-pad" data-testid="section-pricing-preview">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="eyebrow">Transparent Plans</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-brand-navy">No confusion. No hidden surprises.</h2>
        </div>
        <Link to="/pricing" className="btn-cyan">View Full Pricing</Link>
      </div>
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {tiers.map((t) => (
          <div key={t.name} data-testid={t.testid}
            className={`relative card-flat p-7 ${t.featured ? "border-2 border-brand-cyan -translate-y-2 shadow-xl" : ""}`}>
            {t.featured && (
              <div className="absolute -top-3 left-7 bg-brand-cyan text-white text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1">
                Most Preferred by NRIs
              </div>
            )}
            <div className="eyebrow">{t.name}</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display text-4xl font-bold text-brand-navy">{t.price}</span>
              <span className="text-sm text-brand-slate/70">{t.per}</span>
            </div>
            <p className="mt-3 text-sm text-brand-slate/80 leading-relaxed">{t.desc}</p>
            <Link to="/pricing" className={`${t.featured ? "btn-cyan" : "btn-ghost-navy"} mt-6 w-full`}>Choose Plan</Link>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Google reviews mock ---------- */
function GoogleReviews() {
  const reviews = [
    { name: "Suresh K.", loc: "NRI · USA", text: "Hi-Tech Realty handled tenant transition and rent follow-up while I was abroad. Reports are clear with photos. Absolute peace of mind." },
    { name: "Lakshmi R.", loc: "Senior Owner · Adyar", text: "They coordinated repairs, EB payment and tenant calls so I didn't have to. Treated me with respect throughout." },
    { name: "Vikram P.", loc: "Plot Owner · OMR", text: "Boundary issue identified during a routine visit. Saved me from a future legal headache. Highly recommend." },
  ];
  return (
    <section className="section-pad" data-testid="section-google-reviews">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="eyebrow">Real Owners. Real Updates. Real Peace of Mind.</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-brand-navy">Rated by owners across Chennai.</h2>
        </div>
        <div className="flex items-center gap-2 text-brand-navy">
          <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={20} className="fill-amber-400 text-amber-400" />)}</div>
          <span className="text-sm font-semibold">4.9 / 5 · Google Reviews</span>
        </div>
      </div>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {reviews.map((r) => (
          <div key={r.name} className="card-flat p-7">
            <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-amber-400 text-amber-400" />)}</div>
            <p className="mt-4 text-sm text-brand-slate/85 leading-relaxed">"{r.text}"</p>
            <div className="mt-5 font-semibold text-brand-navy">{r.name}</div>
            <div className="text-xs uppercase tracking-[0.22em] text-brand-cyan font-semibold mt-1">{r.loc}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Video Testimonials ---------- */
function Testimonials() {
  const videos = [
    { img: NRI_IMG, t: "NRI Owner · Singapore", q: "I receive updates without calling relatives." },
    { img: SENIOR_IMG, t: "Senior Citizen · Adyar", q: "They helped me manage repairs and tenant issues safely." },
    { img: PLOT_IMG, t: "Plot Owner · OMR", q: "Regular visits helped me identify boundary issues early." },
    { img: HANDSHAKE_IMG, t: "Landlord · Anna Nagar", q: "Rent follow-up and inspection reports reduced my stress." },
  ];
  return (
    <section className="section-pad bg-brand-ice" data-testid="section-testimonials">
      <div className="max-w-4xl">
        <div className="eyebrow">Owner Stories</div>
        <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-brand-navy">Why owners trust us with their property keys, tenants and documents.</h2>
      </div>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {videos.map((v) => (
          <div key={v.t} className="group relative overflow-hidden card-flat">
            <img src={v.img} alt={v.t} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-brand-navy/30 group-hover:bg-brand-navy/50 transition-colors" />
            <button className="absolute inset-0 grid place-items-center" aria-label="Play testimonial">
              <span className="w-14 h-14 bg-white/90 grid place-items-center group-hover:bg-brand-cyan group-hover:text-white transition-colors">
                <Play size={22} />
              </span>
            </button>
            <div className="p-5 bg-white">
              <div className="font-semibold text-brand-navy text-sm">{v.t}</div>
              <p className="text-xs text-brand-slate/70 mt-1">"{v.q}"</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Sample report lead magnet ---------- */
function SampleReport() {
  const [form, setForm] = useState({ name: "", whatsapp: "", property_type: "Apartment", location: "OMR", owner_type: "NRI" });
  const [busy, setBusy] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp) { toast.error("Please share your name and WhatsApp number."); return; }
    setBusy(true);
    try {
      await api.post("/sample-report", form);
      toast.success("Thanks! Sample report link will be sent to your WhatsApp shortly.");
      setForm({ name: "", whatsapp: "", property_type: "Apartment", location: "OMR", owner_type: "NRI" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't submit. Try WhatsApp.");
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-brand-navy text-white" data-testid="section-sample-report">
      <div className="container-x py-20 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="eyebrow !text-brand-cyan">Sample Report</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold leading-tight">
            See what you receive after every inspection.
          </h2>
          <p className="mt-5 text-white/80 leading-relaxed max-w-xl">
            Not sure what to expect? View our sample report — geo-tagged photos, observations, risk status (Green / Amber / Red) and clear action recommendations.
          </p>
          <ul className="mt-6 grid grid-cols-2 gap-3 text-sm text-white/85">
            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-cyan"/> Geo-tagged photos</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-cyan"/> Time-stamped visit log</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-cyan"/> Risk status & rationale</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-cyan"/> Recommended next actions</li>
          </ul>
        </div>
        <form data-testid="sample-report-form" onSubmit={submit} className="lg:col-span-6 bg-white text-brand-slate p-7 border-l-4 border-brand-cyan">
          <div className="font-display text-2xl font-bold text-brand-navy">Download Sample Inspection Report</div>
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <Field label="Full Name" name="name" value={form.name} onChange={onChange} testid="sr-name" />
            <Field label="WhatsApp Number" name="whatsapp" value={form.whatsapp} onChange={onChange} testid="sr-whatsapp" />
            <Select label="Property Type" name="property_type" value={form.property_type} onChange={onChange} testid="sr-property-type"
              options={["Apartment", "Independent House", "Villa", "Vacant Plot", "Commercial"]} />
            <Select label="Location" name="location" value={form.location} onChange={onChange} testid="sr-location"
              options={["OMR", "ECR", "Velachery", "Anna Nagar", "Adyar", "Tambaram", "Porur", "Sholinganallur", "T. Nagar"]} />
            <Select label="Owner Type" name="owner_type" value={form.owner_type} onChange={onChange} testid="sr-owner-type"
              options={["NRI", "Senior Citizen", "Local Owner", "Out-of-Station Owner"]} className="sm:col-span-2" />
          </div>
          <button data-testid="sr-submit" disabled={busy} className="btn-cyan w-full mt-6 disabled:opacity-60">
            {busy ? "Sending…" : "Get Sample Report"}
          </button>
          <p className="text-[11px] mt-3 text-brand-slate/60">Your details are confidential and used only to send the report.</p>
        </form>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, testid, type="text" }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">{label}</span>
      <input data-testid={testid} required type={type} name={name} value={value} onChange={onChange}
        className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan" />
    </label>
  );
}
function Select({ label, name, value, onChange, options, testid, className="" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">{label}</span>
      <select data-testid={testid} name={name} value={value} onChange={onChange}
        className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

/* ---------- FAQ preview ---------- */
function FaqPreview() {
  const faqs = [
    { q: "How do I know a physical inspection actually happened?", a: "Every inspection can be supported with geo-tagged photos, time-stamped images, short videos and a structured inspection report. For premium plans, our field visit is verified through location-based check-in and internal quality review before the report is shared with you. You do not have to simply trust a verbal update — you receive visible proof." },
    { q: "How do you vet tenants to ensure they will not misuse my property?", a: "We follow a structured tenant screening process that may include identity proof collection, employment/business verification, previous address details, reference checks, police verification coordination where required, and clear rental agreement documentation. We also document the move-in condition for a clear baseline from day one." },
    { q: "What happens if you find signs of encroachment on my vacant plot?", a: "We immediately alert you with photos, videos and a risk note. Based on your approval, we coordinate next steps such as cleaning, fencing repair, signboard installation, neighbour enquiry, legal consultation or police complaint support." },
  ];
  return (
    <section className="section-pad" data-testid="section-faq-preview">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="eyebrow">Owner Questions</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold text-brand-navy">Honest answers to the questions that matter.</h2>
        </div>
        <Link to="/faq" className="btn-ghost-navy">View All FAQs</Link>
      </div>
      <Accordion type="multiple" defaultValue={["q0", "q1"]} className="mt-12 max-w-3xl">
        {faqs.map((f, i) => (
          <AccordionItem key={i} value={`q${i}`} data-testid={`faq-item-${i}`} className="border-b border-brand-border">
            <AccordionTrigger className="text-left font-display text-xl text-brand-navy hover:no-underline hover:text-brand-cyan">{f.q}</AccordionTrigger>
            <AccordionContent className="text-sm leading-relaxed text-brand-slate/85">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <a
        href={waLink("Hi Hi-Tech Realty, I still have a question about property management.")}
        target="_blank" rel="noreferrer"
        className="mt-6 inline-block text-sm font-semibold text-brand-cyan hover:underline"
        data-testid="faq-whatsapp-cta"
      >
        Still unsure? Ask us directly on WhatsApp →
      </a>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="bg-brand-navy text-white" data-testid="section-final-cta">
      <div className="container-x py-24 grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-8">
          <div className="eyebrow !text-brand-cyan">Your Property. Our Responsibility.</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold leading-tight">
            Your property should never feel abandoned.
          </h2>
          <p className="mt-4 text-white/80 max-w-2xl">Let Hi-Tech Realty be your trusted local property partner in Chennai.</p>
        </div>
        <div className="md:col-span-4 flex flex-col gap-3">
          <Link to="/contact" data-testid="final-cta-book" className="btn-cyan w-full">Book a Free Consultation</Link>
          <Building2 className="hidden" />
          <a
            href={waLink("Hi Hi-Tech Realty, I want a free property consultation.")}
            target="_blank" rel="noreferrer"
            className="btn-whatsapp w-full"
            data-testid="final-cta-whatsapp"
          >
            Chat with Us Now
          </a>
        </div>
      </div>
    </section>
  );
}
